import { useRef, useState } from 'react';
import { useTranslations } from 'use-intl';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { RouterLinkAs } from '@/lib/router-link';
import {
  Body,
  Box,
  Button,
  Card,
  Eyebrow,
  Field,
  Heading,
  I,
  Input,
  PageHeader,
  RadioCard,
  RadioCardGroup,
  Row,
  Screen,
  ScreenBody,
  Stack,
  StepBadge,
  Textarea,
} from '@leanin/ui';
import { BodyWithAside } from '@/components/page-shell';
import {
  GroupImageUploadField,
  type GroupImageUploadFieldHandle,
} from '@/components/groups/GroupImageUploadField';
import { PlacePicker, type PlaceSelection } from '@/components/geo';
import { useCreateCircle } from '@/lib/mutations/groups';
import { useNetwork } from '@/lib/queries/groups';
import { failureFromError } from '@/lib/api-error';
import { circleSaveErrorMessage } from '@/components/circles/circle-save-error';

type CircleNewSearch = {
  // Optional — when set, the form creates the circle already linked to this
  // network. Drives a contextual back link and a redirect back to the
  // network admin Circles tab on success.
  parentNetworkId?: string;
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const Route = createFileRoute('/_authed/circles/new')({
  validateSearch: (raw: Record<string, unknown>): CircleNewSearch => {
    const v = raw.parentNetworkId;
    // Only accept a well-formed uuid; ignore anything else silently so a
    // malformed link drops back to the standard free-floating form.
    return typeof v === 'string' && UUID_RE.test(v)
      ? { parentNetworkId: v }
      : {};
  },
  component: CircleCreatePage,
});

// Mirrors the onboarding step (/onboarding/cl/create-circle): two cards,
// "The basics" + "How you'll meet".

type Format = 'in_person' | 'virtual' | 'hybrid';
type YesNo = 'yes' | 'no' | null;

function CircleCreatePage() {
  const tc = useTranslations('Circles');
  const { parentNetworkId } = Route.useSearch();
  const tips: ReadonlyArray<{ t: string; s: string }> = [
    { t: tc('new.tip1Title'), s: tc('new.tip1Body') },
    { t: tc('new.tip2Title'), s: tc('new.tip2Body') },
    { t: tc('new.tip3Title'), s: tc('new.tip3Body') },
    { t: tc('new.tip4Title'), s: tc('new.tip4Body') },
    { t: tc('new.tip5Title'), s: tc('new.tip5Body') },
  ];
  // Resolve the parent network (when the page was launched from a network
  // admin surface) so the back link can show the network's name. If the
  // network 404s or the viewer can't read it, we silently fall back to the
  // generic Circles label — the form itself still works and the server is
  // the final authority on the parent-link permission.
  const networkQuery = useNetwork(parentNetworkId ?? '', {
    enabled: parentNetworkId !== undefined,
  });
  const parentNetwork =
    parentNetworkId !== undefined && networkQuery.data !== undefined
      ? networkQuery.data
      : null;

  // Step 01 — The basics
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [place, setPlace] = useState<PlaceSelection | null>(null);
  const [affiliated, setAffiliated] = useState<YesNo>(null);
  const [company, setCompany] = useState('');
  // Step 02 — How you'll meet
  const [openToNew, setOpenToNew] = useState<YesNo>(null);
  const [format, setFormat] = useState<Format>('virtual');
  // Step 03 — Photos. Files are staged in the field handles; the circle
  // doesn't exist until Publish, so the field carries no real group id
  // up front. Publish resolves the id from createCircle, then asks each
  // field to commit using that id.
  const coverFieldRef = useRef<GroupImageUploadFieldHandle | null>(null);
  const [coverPending, setCoverPending] = useState(false);

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [committingPhotos, setCommittingPhotos] = useState(false);
  const navigate = useNavigate();
  const createCircle = useCreateCircle();

  const canSubmit =
    name.trim().length > 0 &&
    place !== null &&
    affiliated !== null &&
    (affiliated === 'no' || company.trim().length > 0) &&
    openToNew !== null;

  const onSubmit = async () => {
    setSubmitError(null);
    if (!canSubmit) {
      setSubmitError(tc('new.validationRequired'));
      return;
    }

    let detail;
    try {
      detail = await createCircle.mutateAsync({
        name: name.trim(),
        ...(description.trim().length > 0
          ? { description: description.trim() }
          : {}),
        place,
        topicIds: [],
        format,
        access: 'public',
        // "Open to new members? Yes" → request-to-join. "No" → invite-only.
        joinPolicy: openToNew === 'yes' ? 'request' : 'invite_only',
        // Tri-state affiliation. `canSubmit` already pinned `affiliated` to
        // a non-null answer, and a 'yes' answer to a non-empty company.
        affiliatedWithCompany: affiliated === 'yes',
        affiliatedCompany: affiliated === 'yes' ? company.trim() : null,
        ...(parentNetworkId !== undefined ? { parentNetworkId } : {}),
      });
    } catch (err) {
      setSubmitError(circleSaveErrorMessage(err, tc('new.createFailed'), tc('errors.linkNotAllowed')));
      return;
    }
    // Photos are optional. If any are staged, commit them under the new
    // circle id (passed as the override so we don't have to wait for a
    // re-render of the field's `groupId` prop). A failed photo upload
    // doesn't block the navigate — the circle exists; the leader can
    // retry photos from circle settings. Just surface the message and
    // route forward.
    if (coverPending) {
      setCommittingPhotos(true);
      try {
        await coverFieldRef.current?.commit(detail.id);
      } catch (err) {
        setSubmitError(
          failureFromError(err, tc('new.photoUploadFailed')).message,
        );
      } finally {
        setCommittingPhotos(false);
      }
    }
    // When the circle was launched from a network admin surface, the
    // user expects to land back on that surface with their new circle
    // already in the list. Otherwise route to the new circle's detail
    // page like a standalone create flow.
    if (parentNetworkId !== undefined) {
      navigate({
        to: '/networks/$id/admin',
        params: { id: parentNetworkId },
        search: { section: 'circles' },
      });
    } else {
      navigate({ to: '/circles/$id', params: { id: detail.id } });
    }
  };
  const isPending = createCircle.isPending || committingPhotos;

  // When launched from a network admin surface, the back link and cancel
  // button retarget to that surface and the back-link label doubles as
  // contextual breadcrumb ("Lean In Saigon ←").
  const cancelHref =
    parentNetworkId !== undefined
      ? `/networks/${parentNetworkId}/admin?section=circles`
      : '/circles';
  const backLabel = parentNetwork !== null ? parentNetwork.name : tc('new.backCircles');

  return (
    <Screen>
      <ScreenBody gap="xl">
        <PageHeader
          eyebrow={tc('new.eyebrow')}
          title={tc('new.title')}
          subtitle={tc('new.subtitle')}
          back={{ label: backLabel, href: cancelHref }}
          actions={
            <>
              <Button href={cancelHref} linkAs={RouterLinkAs} variant="ghost">{tc('new.cancel')}</Button>
              <Button
                variant="primary"
                iconRight={I.arrow}
                onClick={onSubmit}
                disabled={!canSubmit}
                loading={isPending}
              >
                {tc('new.publish')}
              </Button>
            </>
          }
        />
        <BodyWithAside
          asideWidth={380}
          aside={
            <Stack gap="lg">
              <Card pad="default">
                <Stack gap="lg">
                  <Stack gap="xs">
                    <Eyebrow tone="accent">{tc('new.tipsEyebrow')}</Eyebrow>
                    <Heading level={3}>
                      {tc('new.tipsTitle')}
                    </Heading>
                  </Stack>
                  <Stack gap="md">
                    {tips.map((tip, i) => (
                      <Row key={i} gap="sm" align="flex-start">
                        <StepBadge state="current" n={i + 1} size="sm" />
                        <Stack gap="xs">
                          <Heading level={5}>{tip.t}</Heading>
                          <Body size="sm" color="mute">
                            {tip.s}
                          </Body>
                        </Stack>
                      </Row>
                    ))}
                  </Stack>
                </Stack>
              </Card>
            </Stack>
          }
        >
          {/* MAIN */}
          <Stack gap="lg">
            {/* 01 · The basics */}
            <Card pad="spacious">
              <Stack gap="lg">
                <Stack gap="xs">
                  <Eyebrow>{tc('new.step01')}</Eyebrow>
                  <Eyebrow tone="accent">{tc('new.step01Title')}</Eyebrow>
                </Stack>
                <Field required>
                  <Field.Label>{tc('new.nameLabel')}</Field.Label>
                  <Field.Description>
                    {tc('new.nameDescription')}
                  </Field.Description>
                  <Field.Control>
                    <Input
                      placeholder={tc('new.namePlaceholder')}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      maxLength={120}
                    />
                  </Field.Control>
                </Field>
                <Field>
                  <Field.Label>{tc('new.descriptionLabel')}</Field.Label>
                  <Field.Description>
                    {tc('new.descriptionDescription')}
                  </Field.Description>
                  <Field.Control>
                    <Textarea
                      rows={2}
                      placeholder={tc('new.descriptionPlaceholder')}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      maxLength={2000}
                    />
                  </Field.Control>
                </Field>
                <Field required>
                  <Field.Label>{tc('new.locationLabel')}</Field.Label>
                  <Field.Description>
                    {tc('new.locationDescription')}
                  </Field.Description>
                  <Field.Control>
                    <PlacePicker
                      mode="city"
                      value={place}
                      onChange={setPlace}
                      placeholder={tc('new.locationPlaceholder')}
                    />
                  </Field.Control>
                </Field>
                <Field required>
                  <Field.Label>{tc('new.affiliatedQuestion')}</Field.Label>
                  <Field.Description>
                    {tc('new.affiliatedDescription')}
                  </Field.Description>
                  <Field.Control>
                    <RadioCardGroup
                      value={affiliated ?? ''}
                      onValueChange={(v) => setAffiliated(v as 'yes' | 'no')}
                      columns={2}
                      aria-label={tc('new.affiliatedQuestion')}
                    >
                      <RadioCard value="yes" title={tc('new.yes')} />
                      <RadioCard value="no" title={tc('new.no')} />
                    </RadioCardGroup>
                  </Field.Control>
                </Field>
                {affiliated === 'yes' ? (
                  <Field label={tc('new.whichCompany')} required>
                    <Input
                      placeholder={tc('new.companyPlaceholder')}
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                    />
                  </Field>
                ) : null}
              </Stack>
            </Card>

            {/* 02 · How you'll meet */}
            <Card pad="spacious">
              <Stack gap="lg">
                <Stack gap="xs">
                  <Eyebrow>{tc('new.step02')}</Eyebrow>
                  <Eyebrow tone="accent">{tc('new.step02Title')}</Eyebrow>
                </Stack>
                <Field required>
                  <Field.Label>{tc('new.openQuestion')}</Field.Label>
                  <Field.Description>
                    {tc('new.openDescription')}
                  </Field.Description>
                  <Field.Control>
                    <RadioCardGroup
                      value={openToNew ?? ''}
                      onValueChange={(v) => setOpenToNew(v as 'yes' | 'no')}
                      columns={2}
                      aria-label={tc('new.openQuestion')}
                    >
                      <RadioCard value="yes" title={tc('new.yes')} />
                      <RadioCard value="no" title={tc('new.no')} />
                    </RadioCardGroup>
                  </Field.Control>
                </Field>
                <Field label={tc('new.meetFormatLabel')} required>
                  <RadioCardGroup
                    value={format}
                    onValueChange={(v) => setFormat(v as Format)}
                    columns={3}
                    aria-label={tc('new.meetingFormatAria')}
                  >
                    <RadioCard value="virtual" icon={I.globe} title={tc('new.formatVirtual')} />
                    <RadioCard value="in_person" icon={I.pin} title={tc('new.formatInPerson')} />
                    <RadioCard value="hybrid" icon={I.users} title={tc('new.formatHybrid')} />
                  </RadioCardGroup>
                </Field>
              </Stack>
            </Card>

            {/* 03 · Photos */}
            <Card pad="flush">
              <Stack p="xl" gap="xs">
                <Eyebrow>{tc('new.step03')}</Eyebrow>
                <Eyebrow tone="accent">{tc('new.step03Title')}</Eyebrow>
                <Body size="sm" color="mute">
                  {tc('new.photosDescription')}
                </Body>
              </Stack>
              <Box p="xl" style={{ borderTop: '1px solid var(--border)' }}>
                <GroupImageUploadField
                  ref={coverFieldRef}
                  groupKind="circle"
                  // Placeholder identifier — the field doesn't call presign
                  // until commit(), and Publish passes the freshly-created
                  // id as an override at that point.
                  groupId="pending"
                  groupName={name || tc('new.coverFallbackName')}
                  field="cover"
                  currentImageUrl={null}
                  eyebrowOverride={tc('new.coverEyebrow')}
                  onPendingChange={setCoverPending}
                />
              </Box>
            </Card>

            {submitError !== null ? (
              <Card pad="default" tone="burgundy">
                <Body color="primary">
                  {submitError}
                </Body>
              </Card>
            ) : null}

            <Row justify="space-between" align="center">
              <Body size="sm" color="mute">
                {tc('new.publishingHint')}
              </Body>
              <Row gap="sm">
                <Button href={cancelHref} linkAs={RouterLinkAs} variant="ghost">{tc('new.cancel')}</Button>
                <Button
                  variant="primary"
                  iconRight={I.arrow}
                  onClick={onSubmit}
                  disabled={!canSubmit}
                  loading={isPending}
                >
                  {tc('new.publish')}
                </Button>
              </Row>
            </Row>
          </Stack>
        </BodyWithAside>
      </ScreenBody>
    </Screen>
  );
}
